import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import LegalModal from '../components/LegalModal';
import { forgotPasswordApi, resetPasswordApi } from '../services/authApi';

/**
 * Full-screen Login Page - Professional Design
 * Left side: Video background with branding
 * Right side: Login form
 *
 * The same card also asks for a reset email ("Forgot Password?") and sets a new
 * password from a one-time ?reset=<token> link, then drops back to Login with the
 * email filled in, so the person signs in here as usual.
 */
const TITLES = { login: 'Login', forgot: 'Reset password', reset: 'Set new password' };

function LoginPage({ onSwitchToRegister, resetToken = null, onResetDone }) {
    const { login } = useAuth();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [mode, setMode] = useState(resetToken ? 'reset' : 'login'); // 'login' | 'forgot' | 'reset'
    const [notice, setNotice] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);
    const [legalModalType, setLegalModalType] = useState(null); // 'terms' | 'privacy' | 'contact'

    useEffect(() => {
        const handleResize = () => setViewportWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const isTablet = viewportWidth <= 1024;
    const isPhone = viewportWidth <= 768;

    const switchMode = (next) => {
        setMode(next);
        setError('');
        setNotice('');
        setPassword('');
        setConfirmPassword('');
    };

    // Same answer whether or not the account exists (no probing for emails).
    const requestReset = async () => {
        if (!email.trim()) return setError('Enter the email you log in with');
        setIsLoading(true);
        try {
            const r = await forgotPasswordApi(email.trim());
            if (!r.success) return setError(r.message || 'Could not send the reset email');
            switchMode('login');
            setNotice('If that email has an account, a reset link is on its way. It works once, for 1 hour. Check spam too.');
        } catch {
            setError('Could not reach the server. Try again in a minute.');
        } finally {
            setIsLoading(false);
        }
    };

    const setNewPassword = async () => {
        if (password.length < 6) return setError('Password must be at least 6 characters');
        if (password !== confirmPassword) return setError('The two passwords do not match');
        setIsLoading(true);
        try {
            const r = await resetPasswordApi(resetToken, password);
            if (!r.success) return setError(r.message || 'Could not set the password');
            if (onResetDone) onResetDone();
            switchMode('login');
            if (r.email) setEmail(r.email);
            setNotice('Password set, and every other session was signed out. Log in with your new password.');
        } catch {
            setError('Could not reach the server. Try again in a minute.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (mode === 'forgot') return requestReset();
        if (mode === 'reset') return setNewPassword();

        if (!email.trim() || !password) {
            setError('Email and password are required');
            return;
        }

        setIsLoading(true);

        const result = await login(email.trim(), password);
        if (!result.success) {
            setError(result.message || 'Invalid email or password');
            setIsLoading(false);
        }
        // On success, AuthContext updates → App re-renders away from LoginPage
    };

    return (
        <div className={isPhone ? 'm-auth' : undefined} style={{
            ...styles.container,
            flexDirection: isTablet ? 'column' : 'row',
            position: 'relative',
            overflow: isTablet ? 'auto' : 'hidden'
        }}>
            {/* Left Side - Video Background Section (Full-screen underlay on mobile/tablet) */}
            <div style={
                isTablet ? {
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    zIndex: 1,
                    overflow: 'hidden',
                } : {
                    ...styles.videoSection,
                }
            }>
                {/* Video Background */}
                <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    style={styles.video}
                >
                    <source src="/introvid_BERAM.mp4" type="video/mp4" />
                </video>

                {/* Dark Overlay */}
                <div style={styles.videoOverlay}></div>

                {/* Content Layer (only for desktop split screen view) */}
                {!isTablet && (
                    <div style={styles.videoContent}>
                        {/* Top Branding - Logo + BERAM */}
                        <div style={styles.topBranding}>
                            <img
                                src={`${process.env.PUBLIC_URL}/BERAMLOGO.png`}
                                alt="BERAM Logo"
                                style={styles.leftLogo}
                            />
                            <h1 style={styles.brandName}>BERAM</h1>
                        </div>

                        {/* Bottom Section - UTMS Text + Tagline */}
                        <div style={styles.bottomSection}>
                            <p style={styles.brandTagline}>UNMANNED TRAFFIC MANAGEMENT SYSTEM</p>
                            <div style={styles.bottomTagline}>
                                <div style={styles.taglineLine}></div>
                                <span style={styles.taglineText}>FLY BEYOND IMAGINATION</span>
                                <div style={styles.taglineLine}></div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Right Side - Login Panel */}
            <div style={{
                ...styles.loginPanel,
                width: isTablet ? '100%' : styles.loginPanel.width,
                minWidth: isTablet ? '0' : styles.loginPanel.minWidth,
                padding: isPhone ? '20px 16px' : (isTablet ? '32px 24px' : styles.loginPanel.padding),
                borderLeft: isTablet ? 'none' : styles.loginPanel.borderLeft,
                borderTop: 'none',
                position: isTablet ? 'relative' : 'static',
                zIndex: isTablet ? 2 : 'auto',
                background: isTablet ? 'rgba(15, 23, 41, 0.75)' : styles.loginPanel.background,
                backdropFilter: isTablet ? 'blur(16px)' : 'none',
                WebkitBackdropFilter: isTablet ? 'blur(16px)' : 'none',
                minHeight: isTablet ? '100vh' : 'auto',
                flex: isTablet ? 1 : 'unset'
            }}>
                <div style={{
                    ...styles.loginContent,
                    maxWidth: isPhone ? '100%' : styles.loginContent.maxWidth
                }}>
                    {/* Logo Header */}
                    <div style={styles.loginHeader}>
                        <img
                            src={`${process.env.PUBLIC_URL}/BERAMLOGO.png`}
                            alt="BERAM Logo"
                            style={styles.loginLogo}
                        />
                        <h1 style={styles.loginBrand}>BERAM <span style={styles.brandUTMS}>UTMS</span></h1>
                    </div>

                    {/* Login Title */}
                    <h2 style={styles.loginTitle}>{TITLES[mode]}</h2>

                    {/* Login Form */}
                    <form onSubmit={handleSubmit} style={styles.form}>
                        {/* Error Message */}
                        {error && (
                            <div style={styles.errorBox}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <line x1="12" y1="8" x2="12" y2="12"></line>
                                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                                </svg>
                                <span>{error}</span>
                            </div>
                        )}
                        {notice && <div style={styles.noticeBox} role="status">{notice}</div>}
                        {mode === 'forgot' && (
                            <p style={styles.hint}>Enter the email you log in with. We will send a one-time link to set a new password.</p>
                        )}
                        {mode === 'reset' && (
                            <p style={styles.hint}>Choose a new password (at least 6 characters). This link works once.</p>
                        )}

                        {/* Email Field */}
                        {mode !== 'reset' && (
                        <div style={styles.inputGroup}>
                            <label style={styles.label}>Email</label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@example.com"
                                style={styles.input}
                                disabled={isLoading}
                                autoComplete="email"
                                autoFocus={mode === 'forgot'}
                            />
                        </div>
                        )}

                        {/* Password Field */}
                        {mode !== 'forgot' && (
                        <div style={styles.inputGroup}>
                            <label style={styles.label}>{mode === 'reset' ? 'New password' : 'Password'}</label>
                            <div style={styles.passwordWrapper}>
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    style={{...styles.input, paddingRight: '48px'}}
                                    disabled={isLoading}
                                    autoComplete={mode === 'reset' ? 'new-password' : 'current-password'}
                                    autoFocus={mode === 'reset' || (mode === 'login' && !!email)}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    style={styles.eyeButton}
                                >
                                    {showPassword ? (
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                                            <line x1="1" y1="1" x2="23" y2="23"></line>
                                        </svg>
                                    ) : (
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                            <circle cx="12" cy="12" r="3"></circle>
                                        </svg>
                                    )}
                                </button>
                            </div>
                            {mode === 'login' && (
                                <div style={styles.forgotLink}>
                                    <button type="button" onClick={() => switchMode('forgot')} style={styles.linkButton}>Forgot Password?</button>
                                </div>
                            )}
                        </div>
                        )}

                        {mode === 'reset' && (
                            <div style={styles.inputGroup}>
                                <label style={styles.label}>Confirm new password</label>
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    style={styles.input}
                                    disabled={isLoading}
                                    autoComplete="new-password"
                                />
                            </div>
                        )}

                        {/* Login Button */}
                        <button
                            type="submit"
                            style={{
                                ...styles.loginButton,
                                opacity: isLoading ? 0.7 : 1,
                                cursor: isLoading ? 'not-allowed' : 'pointer'
                            }}
                            disabled={isLoading}
                        >
                            {mode === 'forgot' ? (isLoading ? 'Sending...' : 'Send reset link')
                                : mode === 'reset' ? (isLoading ? 'Saving...' : 'Set password')
                                    : (isLoading ? 'Signing in...' : 'Login')}
                        </button>
                        {mode !== 'login' && (
                            <div style={{ textAlign: 'center' }}>
                                <button type="button" onClick={() => switchMode('login')} style={styles.linkButton}>Back to login</button>
                            </div>
                        )}

                        {/* Sign Up Link */}
                        {/* <div style={styles.signupRow}>
                            <span style={styles.signupText}>Don't have an account?</span>
                            <button type="button" onClick={onSwitchToRegister} style={styles.signupLinkButton}>Sign Up</button>
                        </div> */}
                    </form>

                    {/* Footer Links */}
                    <div className="login-footer-links" style={styles.footerLinks}>
                        <button type="button" onClick={() => setLegalModalType('terms')} style={styles.footerLinkButton}>
                            Terms & Conditions
                        </button>
                        <span style={styles.footerDivider}>·</span>
                        <button type="button" onClick={() => setLegalModalType('contact')} style={styles.footerLinkButton}>
                            Contact Us
                        </button>
                        <span style={styles.footerDivider}>·</span>
                        <button type="button" onClick={() => setLegalModalType('privacy')} style={styles.footerLinkButton}>
                            Privacy Policy
                        </button>
                    </div>
                </div>
            </div>

            {/* Legal / Contact Modal */}
            {legalModalType && (
                <LegalModal
                    type={legalModalType}
                    onClose={() => setLegalModalType(null)}
                />
            )}

            {/* Scoped Resets & Keyframes */}
            <style>{`
                @keyframes pulse {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.6; }
                }
                .login-footer-links {
                    display: flex !important;
                    align-items: center !important;
                    justify-content: center !important;
                    gap: 6px !important;
                    flex-wrap: wrap !important;
                }
                .login-footer-links button {
                    width: auto !important;
                    margin: 0 !important;
                    padding: 3px 6px !important;
                    text-transform: none !important;
                    letter-spacing: normal !important;
                    background: transparent !important;
                    font-weight: 500 !important;
                    font-size: 11.5px !important;
                    color: #849396 !important;
                    text-decoration: none !important;
                    display: inline-flex !important;
                    align-items: center !important;
                    border: none !important;
                    cursor: pointer !important;
                    transition: color 0.15s ease !important;
                }
                .login-footer-links button:hover {
                    color: #00daf3 !important;
                    text-decoration: underline !important;
                    transform: none !important;
                }
            `}</style>
        </div>
    );
}

const styles = {
    container: {
        display: 'flex',
        minHeight: '100vh',
        background: '#0a1220',
        fontFamily: "'Inter', 'Segoe UI', sans-serif",
    },

    // ========== LEFT SIDE - VIDEO SECTION ==========
    videoSection: {
        flex: 1,
        position: 'relative',
        overflow: 'hidden',
        minHeight: '100vh',
    },
    video: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
    },
    videoOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(180deg, rgba(10, 18, 32, 0.9) 0%, rgba(10, 18, 32, 0.75) 40%, rgba(10, 18, 32, 0.85) 100%)',
    },
    videoContent: {
        position: 'relative',
        zIndex: 10,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '60px 50px',
        boxSizing: 'border-box',
    },

    // Top Branding - Logo + BERAM
    topBranding: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '20px',
    },
    leftLogo: {
        width: '120px',
        height: '120px',
        filter: 'drop-shadow(0 0 30px rgba(34, 211, 238, 0.4))',
    },
    brandName: {
        margin: 0,
        fontSize: '48px',
        fontWeight: 800,
        color: '#fff',
        letterSpacing: '10px',
        textTransform: 'uppercase',
    },

    // Bottom Section - UTMS + Tagline
    bottomSection: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '24px',
        width: '100%',
    },
    brandTagline: {
        margin: 0,
        fontSize: '24px',
        color: 'rgba(255, 255, 255, 0.8)',
        letterSpacing: '6px',
        textTransform: 'uppercase',
        fontWeight: 600,
    },
    bottomTagline: {
        display: 'flex',
        alignItems: 'center',
        gap: '20px',
        width: '100%',
    },
    taglineLine: {
        flex: 1,
        height: '1px',
        background: 'linear-gradient(90deg, transparent, rgba(34, 211, 238, 0.4), transparent)',
    },
    taglineText: {
        fontSize: '14px',
        fontWeight: 600,
        color: '#22d3ee',
        letterSpacing: '5px',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
    },

    // ========== RIGHT SIDE - LOGIN PANEL ==========
    loginPanel: {
        width: '460px',
        minWidth: '400px',
        background: '#0f1729',
        borderLeft: '1px solid rgba(34, 211, 238, 0.15)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px',
        boxSizing: 'border-box',
    },
    loginContent: {
        width: '100%',
        maxWidth: '320px',
    },

    // Login Header
    loginHeader: {
        textAlign: 'center',
        marginBottom: '32px',
    },
    loginLogo: {
        width: '64px',
        height: '64px',
        marginBottom: '16px',
        filter: 'drop-shadow(0 0 15px rgba(34, 211, 238, 0.25))',
    },
    loginBrand: {
        margin: 0,
        fontSize: '22px',
        fontWeight: 700,
        color: '#fff',
        letterSpacing: '2px',
    },
    brandUTMS: {
        color: '#22d3ee',
        fontWeight: 400,
    },

    // Login Title
    loginTitle: {
        margin: '0 0 28px 0',
        fontSize: '22px',
        fontWeight: 600,
        color: '#3b82f6',
        textAlign: 'center',
    },

    // Form Styles
    form: {
        display: 'flex',
        flexDirection: 'column',
        gap: '18px',
    },
    errorBox: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '12px 14px',
        background: 'rgba(239, 68, 68, 0.1)',
        border: '1px solid rgba(239, 68, 68, 0.5)',
        borderRadius: '6px',
        color: '#f87171',
        fontSize: '13px',
    },
    inputGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    },
    label: {
        fontSize: '13px',
        fontWeight: 500,
        color: '#94a3b8',
    },
    input: {
        width: '100%',
        padding: '14px 16px',
        background: '#1a2640',
        border: '1px solid rgba(71, 85, 105, 0.5)',
        borderRadius: '8px',
        color: '#fff',
        fontSize: '14px',
        outline: 'none',
        transition: 'all 0.2s ease',
        boxSizing: 'border-box',
    },
    passwordWrapper: {
        position: 'relative',
        width: '100%',
    },
    eyeButton: {
        position: 'absolute',
        right: '10px',
        top: '50%',
        transform: 'translateY(-50%)',
        background: 'transparent',
        border: 'none',
        color: '#64748b',
        cursor: 'pointer',
        padding: 0,
        width: '28px',
        height: '28px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2,
    },
    noticeBox: {
        padding: '12px 14px',
        background: 'rgba(16, 185, 129, 0.1)',
        border: '1px solid rgba(16, 185, 129, 0.45)',
        borderRadius: '6px',
        color: '#34d399',
        fontSize: '13px',
        lineHeight: 1.5,
    },
    hint: {
        margin: 0,
        fontSize: '13px',
        color: '#94a3b8',
        lineHeight: 1.5,
    },
    forgotLink: {
        textAlign: 'right',
        marginTop: '4px',
    },
    linkButton: {
        fontSize: '12px',
        color: '#3b82f6',
        textDecoration: 'none',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: 0,
        fontFamily: 'inherit',
    },

    // Login Button
    loginButton: {
        width: '100%',
        padding: '14px',
        background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
        border: 'none',
        borderRadius: '8px',
        color: '#fff',
        fontSize: '15px',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        marginTop: '8px',
    },

    // Sign Up
    signupRow: {
        textAlign: 'center',
        marginTop: '8px',
    },
    signupText: {
        fontSize: '13px',
        color: '#64748b',
    },
    signupLinkButton: {
        fontSize: '13px',
        color: '#22d3ee',
        textDecoration: 'none',
        marginLeft: '6px',
        fontWeight: 500,
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: 0,
        fontFamily: 'inherit',
    },

    // Demo Box
    demoBox: {
        marginTop: '24px',
        padding: '10px 14px',
        background: 'rgba(34, 211, 238, 0.05)',
        border: '1px solid rgba(34, 211, 238, 0.2)',
        borderRadius: '6px',
        textAlign: 'center',
    },
    demoLabel: {
        fontSize: '11px',
        color: '#64748b',
        marginRight: '8px',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    },
    demoCode: {
        fontSize: '12px',
        color: '#22d3ee',
        fontFamily: 'monospace',
    },

    // Footer Links
    footerLinks: {
        marginTop: '32px',
        textAlign: 'center',
        display: 'flex',
        justifyContent: 'center',
        flexWrap: 'wrap',
        gap: '8px',
    },
    footerLinkButton: {
        fontSize: '11px',
        color: '#64748b',
        textDecoration: 'underline',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: 0,
        fontFamily: 'inherit',
    },
    footerDivider: {
        fontSize: '11px',
        color: '#475569',
    },
};

export default LoginPage;
